import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProductSelector from './ProductSelector';
import PaymentMethodPicker from './PaymentMethodPicker';
import { Product, PaymentMethod, PaymentMethodType } from '../../../types/payment';
import { ExclamationCircleIcon as XCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import kakaoPayService from '../../../services/payment/kakaoPayService';
import { useToast } from '../../../contexts';

// Mock data - 실제로는 API에서 가져옴
const mockProducts: Product[] = [
  {
    id: '1',
    name: '1개월 이용권',
    description: '독서실 1개월 자유이용권',
    price: 150000,
    discountPrice: 120000,
    currency: 'KRW',
    category: 'subscription',
    duration: 30,
    features: ['24시간 이용 가능', '좌석 예약 무제한', '스터디룸 할인'],
    popular: false
  },
  {
    id: '2',
    name: '3개월 이용권',
    description: '독서실 3개월 자유이용권',
    price: 450000,
    discountPrice: 330000,
    currency: 'KRW',
    category: 'subscription',
    duration: 90,
    features: ['24시간 이용 가능', '좌석 예약 무제한', '스터디룸 할인', '사물함 무료'],
    popular: true
  },
  {
    id: '3',
    name: '6개월 이용권',
    description: '독서실 6개월 자유이용권',
    price: 900000,
    discountPrice: 600000,
    currency: 'KRW',
    category: 'subscription',
    duration: 180,
    features: ['24시간 이용 가능', '좌석 예약 무제한', '스터디룸 할인', '사물함 무료', 'VIP 라운지 이용'],
    popular: false
  }
];

const mockSavedMethods: PaymentMethod[] = [
  {
    id: '1',
    type: PaymentMethodType.CARD,
    name: '신한카드 1234',
    cardNumber: '****-****-****-1234',
    cardCompany: '신한카드',
    isDefault: true,
    isExpired: false,
    expiresAt: '2025-12-31'
  }
];

const PaymentPageV2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showWarning, showInfo } = useToast();
  const [selectedProduct, setSelectedProduct] = useState<Product>(mockProducts[1]); // 기본값
  const [selectedMethodId, setSelectedMethodId] = useState<string>('');
  const [selectedMethodType, setSelectedMethodType] = useState<PaymentMethodType>(PaymentMethodType.KAKAO_PAY);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // StudentDashboard에서 전달받은 상품 정보 처리
  useEffect(() => {
    const state = location.state as { selectedProduct?: Product; returnTo?: string };
    if (state?.selectedProduct) {
      setSelectedProduct(state.selectedProduct);
    }
  }, [location.state]);

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleMethodSelect = (methodId: string, methodType: PaymentMethodType) => {
    setSelectedMethodId(methodId);
    setSelectedMethodType(methodType);
  };

  const handlePayment = async () => {
    // 입력 검증
    if (!selectedProduct) {
      showError('상품 정보가 없습니다. 다시 시도해주세요.');
      return;
    }

    if (!termsAgreed) {
      showWarning('약관에 동의해주세요');
      return;
    }

    if (!selectedMethodType && !selectedMethodId) {
      showWarning('결제 수단을 선택해주세요');
      return;
    }

    // 이미 처리 중인 경우 중복 실행 방지
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    try {
      // 상품 정보 재검증
      if (!selectedProduct.id || !selectedProduct.name || typeof selectedProduct.price !== 'number') {
        throw new Error('상품 정보가 유효하지 않습니다.');
      }

      // 금액 검증 (0원 또는 음수 방지)
      const totalAmount = calculateTotal();
      if (totalAmount <= 0) {
        throw new Error('결제 금액이 유효하지 않습니다.');
      }

      console.log('💳 [PaymentPageV2] 결제 시작:', {
        product: selectedProduct.name,
        amount: totalAmount,
        method: selectedMethodType
      });

      // 카카오페이 결제
      if (selectedMethodType === PaymentMethodType.KAKAO_PAY) {
        const response = await kakaoPayService.preparePayment({
          productId: selectedProduct.id,
          quantity: 1,
          taxFreeAmount: 0
        });

        if (!response || !response.success) {
          const errorMessage = (response as any)?.message || '결제 준비에 실패했습니다';
          throw new Error(errorMessage);
        }

        // 카카오페이 리다이렉트 처리 (성공 시 자동으로 리다이렉트됨)

      } else if (selectedMethodId) {
        // 저장된 카드 결제
        showInfo('저장된 카드 결제는 준비 중입니다');
        
      } else {
        // 기타 결제 수단
        throw new Error('지원하지 않는 결제 수단입니다.');
      }

    } catch (error) {
      console.error('❌ [PaymentPageV2] 결제 처리 실패:', error);
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : '결제 처리 중 오류가 발생했습니다';
      
      showError(errorMessage);
      
      // 네트워크 오류인 경우 추가 안내
      if (error instanceof TypeError && error.message.includes('fetch')) {
        showWarning('네트워크 연결을 확인해주세요');
      }
      
    } finally {
      setIsProcessing(false);
    }
  };

  const calculateTotal = () => {
    return selectedProduct.discountPrice || selectedProduct.price;
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">이용권 구매</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 왼쪽: 상품 선택, 결제 수단 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 상품 선택 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold mb-4">상품 선택</h2>
              <ProductSelector
                products={mockProducts}
                selectedProductId={selectedProduct.id}
                onSelect={handleProductSelect}
              />
            </div>

            {/* 결제 수단 선택 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold mb-4">결제 수단</h2>
              <PaymentMethodPicker
                savedMethods={mockSavedMethods}
                selectedMethodId={selectedMethodId}
                selectedMethodType={selectedMethodType}
                onSelectMethod={handleMethodSelect}
                allowedMethods={[
                  PaymentMethodType.KAKAO_PAY,
                  PaymentMethodType.CARD,
                  PaymentMethodType.BANK_TRANSFER
                ]}
              />
            </div>

            {/* 약관 동의 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold mb-4">약관 동의</h2>
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAgreed}
                    onChange={(e) => setTermsAgreed(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">
                      결제 서비스 이용약관 동의 (필수)
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      결제 서비스 이용약관 및 개인정보 처리방침에 모두 동의합니다.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* 오른쪽: 결제 요약 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <h3 className="text-lg font-bold mb-4">결제 정보</h3>

              {/* 선택된 상품 정보 */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">상품명</span>
                  <span className="font-medium">{selectedProduct.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">정가</span>
                  <span className={selectedProduct.discountPrice ? 'line-through text-gray-400' : 'font-medium'}>
                    {formatAmount(selectedProduct.price)}
                  </span>
                </div>
                {selectedProduct.discountPrice && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">할인가</span>
                    <span className="font-medium text-red-600">
                      {formatAmount(selectedProduct.discountPrice)}
                    </span>
                  </div>
                )}
              </div>

              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">총 결제 금액</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {formatAmount(calculateTotal())}
                  </span>
                </div>
              </div>

              {/* 결제 버튼 */}
              <button
                onClick={handlePayment}
                disabled={!termsAgreed || isProcessing}
                className={`w-full py-4 rounded-lg font-medium transition-all ${
                  termsAgreed && !isProcessing
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    처리 중...
                  </span>
                ) : (
                  `${formatAmount(calculateTotal())} 결제하기`
                )}
              </button>

              {/* 안내 메시지 */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <XCircleIcon className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-gray-600">
                    <p>• 결제 완료 후 즉시 이용 가능합니다</p>
                    <p>• 환불 규정은 이용약관을 참고해주세요</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPageV2;